"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, useSyncExternalStore, type CSSProperties } from "react";
import { Accordion } from "@repo/ui/mantine";
import { Typography } from "@repo/ui/aops-components/typography";
import { cmsBrandColorCssVar } from "@repo/ui/lib/cmsBrandColors";
import { cn } from "@repo/ui/lib/utils";
import { SanityMediaFieldContent } from "@repo/sanity-utils/components/media/SanityMediaFieldContent";
import type { DiscoveryPageQueryResult } from "@repo/sanity-utils/schema/types";
import { Minus, Plus } from "lucide-react";
import type { ResolvedSanityImage } from "@/lib/discoveryMedia";
import { AopsPortableText } from "../../PortableText/PortableTextComponents";
import styles from "./DiscoveryAccordionBlock.module.css";
import { getDefaultOpenKey, resolveOpenAccordionKey, shouldAcceptAccordionChange } from "./DiscoveryAccordionBlockUtils";
type DiscoveryPageBlocks = NonNullable<NonNullable<DiscoveryPageQueryResult>["pageContent"]>;
export type DiscoveryAccordionBlockData = Extract<DiscoveryPageBlocks[number], {
  _type: "discoveryAccordionBlock";
}>;
type AccordionItem = NonNullable<DiscoveryAccordionBlockData["items"]>[number];
export type DiscoveryAccordionItemMedia = {
  imageData?: ResolvedSanityImage;
  videoUrl?: string | null;
  alt?: string;
};
type DiscoveryAccordionBlockProps = {
  block: DiscoveryAccordionBlockData;
  mediaByKey: Record<string, DiscoveryAccordionItemMedia>;
};
type PlayMode = "idle" | "auto" | "manual" | "done";
type AccentBarStyle = CSSProperties & {
  "--section-duration"?: string;
};
const DEFAULT_SECTION_DURATION_MS = 5000;
const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const EMPTY_ITEMS: AccordionItem[] = [];
function subscribeToMediaQuery(query: string, onStoreChange: () => void) {
  const mediaQuery = window.matchMedia(query);
  mediaQuery.addEventListener("change", onStoreChange);
  return () => mediaQuery.removeEventListener("change", onStoreChange);
}
function useMediaQuery(query) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== query) {
    t0 = onStoreChange => subscribeToMediaQuery(query, onStoreChange);
    t1 = () => window.matchMedia(query).matches;
    $[0] = query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp);
}
function _temp() {
  return false;
}
function AccordionItemMedia(t0) {
  const $ = _c(12);
  const {
    media,
    className,
    sizes
  } = t0;
  if (!media?.imageData && !media?.videoUrl) {
    return null;
  }
  let t1;
  if ($[0] !== className) {
    t1 = cn(styles.mediaFrame, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== media.alt || $[3] !== media.imageData) {
    t2 = media.imageData ? {
      src: media.imageData.url,
      alt: media.alt ?? "",
      width: media.imageData.width,
      height: media.imageData.height
    } : null;
    $[2] = media.alt;
    $[3] = media.imageData;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = media.imageData ? null : media.videoUrl ?? null;
  let t4;
  if ($[5] !== sizes || $[6] !== t2 || $[7] !== t3) {
    t4 = <SanityMediaFieldContent image={t2} videoUrl={t3} imageClassName={styles.mediaImage} videoClassName={styles.mediaVideo} sizes={sizes} fill={true} />;
    $[5] = sizes;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className={t1}>{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function DiscoveryAccordionBlock(t0) {
  const $ = _c(59);
  const {
    block,
    mediaByKey
  } = t0;
  const items = block.items ?? EMPTY_ITEMS;
  const sectionRef = useRef(null);
  let t1;
  if ($[0] !== items) {
    t1 = () => getDefaultOpenKey(items);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [opened, setOpened] = useState(t1);
  const [mode, setMode] = useState("idle");
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const canAutoplay = isDesktop && !prefersReducedMotion;
  let t2;
  if ($[2] !== isDesktop || $[3] !== items || $[4] !== opened) {
    const defaultOpenKey = getDefaultOpenKey(items);
    t2 = resolveOpenAccordionKey(opened, isDesktop, defaultOpenKey);
    $[2] = isDesktop;
    $[3] = items;
    $[4] = opened;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const openKey = t2;
  const durationMs = typeof block.sectionDurationMs === "number" && block.sectionDurationMs > 0 ? block.sectionDurationMs : DEFAULT_SECTION_DURATION_MS;
  let t3;
  let t4;
  if ($[6] !== canAutoplay || $[7] !== mode) {
    t3 = () => {
      if (mode !== "idle" || !canAutoplay) {
        return;
      }
      const el = sectionRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries.some(_temp2)) {
          setMode("auto");
          observer.disconnect();
        }
      }, {
        threshold: 0.75
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t4 = [mode, canAutoplay];
    $[6] = canAutoplay;
    $[7] = mode;
    $[8] = t3;
    $[9] = t4;
  } else {
    t3 = $[8];
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[10] !== canAutoplay || $[11] !== durationMs || $[12] !== items || $[13] !== mode || $[14] !== openKey) {
    t5 = () => {
      if (mode !== "auto" || !canAutoplay) {
        return;
      }
      const timeoutId = window.setTimeout(() => {
        const currentIndex = items.findIndex(item => item._key === openKey);
        if (currentIndex < 0) {
          return;
        }
        if (currentIndex >= items.length - 1) {
          setMode("done");
          return;
        }
        const nextItem = items[currentIndex + 1];
        if (nextItem) {
          setOpened(nextItem._key);
        }
      }, durationMs);
      return () => window.clearTimeout(timeoutId);
    };
    t6 = [mode, openKey, canAutoplay, durationMs, items];
    $[10] = canAutoplay;
    $[11] = durationMs;
    $[12] = items;
    $[13] = mode;
    $[14] = openKey;
    $[15] = t5;
    $[16] = t6;
  } else {
    t5 = $[15];
    t6 = $[16];
  }
  useEffect(t5, t6);
  if (items.length === 0) {
    return null;
  }
  const openMedia = openKey ? mediaByKey[openKey] : undefined;
  const barIsAnimating = mode === "auto" && canAutoplay;
  const barIsFilled = mode === "manual" || mode === "done" || prefersReducedMotion;
  let t7;
  if ($[17] !== block.eyebrow) {
    t7 = block.eyebrow ? <Typography variant="eyebrow" as="p" className={styles.eyebrow}>{block.eyebrow}</Typography> : null;
    $[17] = block.eyebrow;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== block.title) {
    t8 = block.title ? <><Typography variant="h4" as="h2" className={cn(styles.title, styles.titleMobile)}>{block.title}</Typography><Typography variant="h3" as="span" className={cn(styles.title, styles.titleTablet)} aria-hidden={true}>{block.title}</Typography><Typography variant="h2" as="span" className={cn(styles.title, styles.titleDesktop)} aria-hidden={true}>{block.title}</Typography></> : null;
    $[19] = block.title;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t7 || $[22] !== t8) {
    t9 = <header className={styles.header}>{t7}{t8}</header>;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const t10 = openMedia == null;
  let t11;
  if ($[24] !== openMedia) {
    t11 = <AccordionItemMedia media={openMedia} sizes="(min-width: 1024px) 50vw, 0px" />;
    $[24] = openMedia;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div className={styles.sideMedia} aria-hidden={t10}>{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== isDesktop) {
    t13 = value => {
      if (!shouldAcceptAccordionChange(value, isDesktop)) {
        return;
      }
      setOpened(value);
      setMode("manual");
    };
    $[29] = isDesktop;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      root: styles.accordionRoot,
      item: styles.accordionItem,
      control: styles.accordionControl,
      label: styles.accordionLabel,
      panel: styles.accordionPanel,
      content: styles.accordionContent
    };
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== barIsAnimating || $[33] !== barIsFilled || $[34] !== durationMs || $[35] !== items || $[36] !== mediaByKey || $[37] !== mode || $[38] !== openKey || $[39] !== opened) {
    let t16;
    if ($[41] !== barIsAnimating || $[42] !== barIsFilled || $[43] !== durationMs || $[44] !== mediaByKey || $[45] !== mode || $[46] !== openKey || $[47] !== opened) {
      t16 = item_0 => {
        const isOpen = item_0._key === openKey;
        const accent = cmsBrandColorCssVar(item_0.accentColor) ?? "var(--color-surface-accent-yellow)";
        const itemMedia = mediaByKey[item_0._key];
        const accentBarStyle = {
          backgroundColor: accent,
          "--section-duration": `${durationMs}ms`
        };
        return <Accordion.Item key={item_0._key} value={item_0._key} className={cn(isOpen && styles.accordionItemOpen)}>{isOpen ? <div key={`${item_0._key}-${barIsAnimating ? opened : mode}`} className={cn(styles.accentBar, barIsAnimating && styles.accentBarAnimating, barIsFilled && styles.accentBarFilled)} style={accentBarStyle} aria-hidden={true} /> : null}<Accordion.Control><span className={styles.controlInner}><Typography variant="h5" as="span" className={cn(styles.itemTitle, styles.itemTitleCompact)}>{item_0.title}</Typography><Typography variant="h4" as="span" className={cn(styles.itemTitle, styles.itemTitleDesktop)} aria-hidden={true}>{item_0.title}</Typography><span className={styles.chevron} aria-hidden={true}>{isOpen ? <Minus size={24} strokeWidth={1.5} /> : <Plus size={24} strokeWidth={1.5} />}</span></span></Accordion.Control><Accordion.Panel>{Array.isArray(item_0.body) && item_0.body.length > 0 ? <div className={styles.itemBody}><AopsPortableText value={item_0.body} /></div> : null}<AccordionItemMedia media={itemMedia} className={styles.inlineMedia} sizes="(max-width: 1023px) 100vw, 0px" /></Accordion.Panel></Accordion.Item>;
      };
      $[41] = barIsAnimating;
      $[42] = barIsFilled;
      $[43] = durationMs;
      $[44] = mediaByKey;
      $[45] = mode;
      $[46] = openKey;
      $[47] = opened;
      $[48] = t16;
    } else {
      t16 = $[48];
    }
    t15 = items.map(t16);
    $[32] = barIsAnimating;
    $[33] = barIsFilled;
    $[34] = durationMs;
    $[35] = items;
    $[36] = mediaByKey;
    $[37] = mode;
    $[38] = openKey;
    $[39] = opened;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[49] !== openKey || $[50] !== t13 || $[51] !== t15) {
    t16 = <Accordion variant="unstyled" value={openKey} onChange={t13} chevron={null} classNames={t14}>{t15}</Accordion>;
    $[49] = openKey;
    $[50] = t13;
    $[51] = t15;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t12 || $[54] !== t16) {
    t17 = <div className={styles.panel}>{t12}{t16}</div>;
    $[53] = t12;
    $[54] = t16;
    $[55] = t17;
  } else {
    t17 = $[55];
  }
  let t18;
  if ($[56] !== t17 || $[57] !== t9) {
    t18 = <div ref={sectionRef} className={styles.section}>{t9}{t17}</div>;
    $[56] = t17;
    $[57] = t9;
    $[58] = t18;
  } else {
    t18 = $[58];
  }
  return t18;
}
function _temp2(entry) {
  return entry.isIntersecting;
}